import { useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CheckCircle2, Mail } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { track } from "../lib/analytics";
import { Seo } from "../components/site/Seo";
import { PageHeader } from "../components/site/Blocks";
import { BRAND } from "../lib/brand";

const CATS = [
  ["opci_upit", "Opći upit"], ["dojava_vijesti", "Dojava vijesti"], ["saradnja", "Saradnja i partnerstva"],
  ["marketing", "Marketing i oglašavanje"], ["ispravka", "Prijava greške"], ["gegendarstellung", "Zahtjev za objavu odgovora (Gegendarstellung)"],
];

const Field = ({ label, id, children, hint }) => (
  <div>
    <label htmlFor={id} className="mb-1.5 block text-sm font-semibold">{label}</label>
    {children}
    {hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
  </div>
);

const inputCls = "w-full rounded-sm border border-input bg-card px-3 py-2.5 text-sm outline-none transition-shadow focus:ring-2 focus:ring-ring";

export default function ContactPage() {
  const [params] = useSearchParams();
  const started = useRef(Date.now());
  const initialCat = CATS.some(([k]) => k === params.get("kategorija")) ? params.get("kategorija") : "opci_upit";
  const [f, setF] = useState({ category: initialCat, name: "", email: "", subject: "", message: "", article_url: "", consent: false, website: "" });
  const [state, setState] = useState({ sending: false, done: false, error: "" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    const errs = [];
    if (f.name.trim().length < 2) errs.push("Unesite ime i prezime.");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email)) errs.push("Unesite ispravnu email adresu.");
    if (f.subject.trim().length < 3) errs.push("Naslov mora imati najmanje 3 znaka.");
    if (f.message.trim().length < 20) errs.push("Poruka mora imati najmanje 20 znakova.");
    if (!f.consent) errs.push("Potvrdite da ste pročitali informacije o zaštiti podataka.");
    if (errs.length) return setState({ sending: false, done: false, error: errs.join(" ") });
    setState({ sending: true, done: false, error: "" });
    try {
      await api.post("/public/contact", { ...f, form_started_at: started.current });
      track("contact_form_success", { category: (f.category || "opce").replace(/[^\w-]/g, "").slice(0, 40) });
      setState({ sending: false, done: true, error: "" });
    } catch (err) {
      setState({ sending: false, done: false, error: errMsg(err) });
    }
  };

  return (
    <div className="container-news" data-testid="contact-page">
      <Seo title="Kontakt" description="Kontaktirajte redakciju BalkanPuls.de – upiti, dojave, saradnja, oglašavanje i ispravke." />
      <PageHeader kicker="Kontakt" title="Pišite redakciji" intro="Dojave, pitanja, prijedlozi za saradnju ili zahtjevi za ispravku – svaka poruka stiže direktno u redakciju." />
      <div className="mt-10 grid gap-12 lg:grid-cols-12">
        <aside className="space-y-6 lg:col-span-4">
          <div className="border border-border bg-card p-6">
            <Mail className="h-5 w-5 text-pulse" aria-hidden />
            <p className="mt-3 text-sm text-muted-foreground">Centralna adresa redakcije</p>
            <a href={`mailto:${BRAND.email}`} className="font-display text-2xl font-semibold text-ink hover:text-pulse" data-testid="contact-email-link">{BRAND.email}</a>
          </div>
          <div className="text-sm leading-relaxed text-muted-foreground" data-testid="contact-privacy-info">
            <h2 className="mb-2 font-semibold text-foreground">Zaštita podataka</h2>
            <p>Podatke iz forme (ime, email, poruka, link na članak) koristimo za obradu i odgovor na vaš upit (čl. 6 st. 1 lit. b ili f GDPR; kod zahtjeva za objavu odgovora lit. c). Dostupni su samo ovlaštenim članovima redakcije i ne objavljuju se uz ispravke. Brisanje možete zatražiti na {BRAND.email}. Više u <a href="/datenschutz" className="underline" data-testid="contact-datenschutz-link">Izjavi o zaštiti podataka</a>.</p>
          </div>
        </aside>
        <div className="lg:col-span-8">
          {state.done ? (
            <div className="border border-emerald-300 bg-emerald-50 p-8 text-emerald-950 dark:bg-emerald-950/40 dark:text-emerald-100" data-testid="contact-success">
              <CheckCircle2 className="h-7 w-7" />
              <h2 className="mt-3 font-display text-2xl font-semibold">Poruka je zaprimljena</h2>
              <p className="mt-2 text-sm">Vaša poruka je sačuvana i proslijeđena redakciji na obradu. Za hitne slučajeve pišite direktno na {BRAND.email}.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="grid gap-5 sm:grid-cols-2" noValidate data-testid="contact-form">
              <div className="sm:col-span-2">
                <Field label="Kategorija upita" id="c-cat">
                  <select id="c-cat" value={f.category} onChange={set("category")} className={inputCls} data-testid="contact-category-select">
                    {CATS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                  </select>
                </Field>
              </div>
              <Field label="Ime i prezime" id="c-name"><input id="c-name" required minLength={2} maxLength={100} value={f.name} onChange={set("name")} className={inputCls} data-testid="contact-name-input" /></Field>
              <Field label="Email" id="c-email"><input id="c-email" type="email" required value={f.email} onChange={set("email")} className={inputCls} data-testid="contact-email-input" /></Field>
              <div className="sm:col-span-2"><Field label="Naslov" id="c-subj"><input id="c-subj" required minLength={3} maxLength={160} value={f.subject} onChange={set("subject")} className={inputCls} data-testid="contact-subject-input" /></Field></div>
              {["ispravka", "gegendarstellung"].includes(f.category) && (
                <div className="sm:col-span-2"><Field label="Link na članak (opcionalno)" id="c-url"><input id="c-url" type="url" value={f.article_url} onChange={set("article_url")} className={inputCls} data-testid="contact-article-url-input" /></Field></div>
              )}
              {f.category === "ispravka" && <p className="text-xs text-muted-foreground sm:col-span-2" data-testid="contact-correction-hint">Navedite spornu tvrdnju, šta smatrate tačnim i, ako imate, izvor. Dokumenti i nepotrebni lični podaci nisu potrebni. <a href="/korrekturen" className="underline">Kako postupamo s ispravkama</a></p>}
              {f.category === "gegendarstellung" && <p className="rounded-sm bg-amber-50 p-3 text-xs text-amber-950 sm:col-span-2" data-testid="contact-gd-hint">Zahtjev za objavu odgovora (§ 20 MStV) obrađuje odgovorna osoba odvojeno od običnih ispravki. Za ovaj zahtjev mogu važiti posebni zakonski uslovi forme i rokovi – po potrebi ga pošaljite i poštom na adresu iz <a href="/impressum" className="underline">Impressuma</a>.</p>}
              <div className="sm:col-span-2"><Field label="Poruka" id="c-msg" hint="Najmanje 20 znakova."><textarea id="c-msg" required minLength={20} maxLength={5000} rows={7} value={f.message} onChange={set("message")} className={inputCls} data-testid="contact-message-input" /></Field></div>
              <div className="hidden" aria-hidden="true"><label>Website<input tabIndex={-1} autoComplete="off" value={f.website} onChange={set("website")} data-testid="contact-honeypot" /></label></div>
              <label className="flex items-start gap-3 text-sm sm:col-span-2">
                <input type="checkbox" required checked={f.consent} onChange={set("consent")} className="mt-1 h-4 w-4 accent-[#E8481C]" data-testid="contact-consent-checkbox" />
                <span>Pročitao/la sam <a href="/datenschutz" className="underline">informacije o zaštiti podataka</a>. Podaci se koriste samo za obradu ovog upita.</span>
              </label>
              {state.error && <p role="alert" className="text-sm font-medium text-destructive sm:col-span-2" data-testid="contact-error">{state.error}</p>}
              <div className="sm:col-span-2">
                <button type="submit" disabled={state.sending} className="h-12 rounded-sm bg-navy px-8 text-sm font-semibold text-white transition-colors hover:bg-navy-700 disabled:opacity-60 dark:bg-pulse" data-testid="contact-submit-button">
                  {state.sending ? "Slanje…" : "Pošalji poruku"}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
